import { useQuery } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks/shared/useAccessToken";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import {
  fetchTenantAccount,
  type TenantWithRegion,
} from "@/lib/utils/tenant-account";
import { TenantApi } from "@/openapi/generated";

interface IUseTenantGetProps {
  tenantId: number;
}

export const useTenantGet = (
  { tenantId }: IUseTenantGetProps,
  options?: { enabled?: boolean },
) => {
  const accessToken = useAccessToken();

  const {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [QueryKeys.Tenant, tenantId],
    queryFn: ({ client, queryKey }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(TenantApi, accessToken);
      const previous = client.getQueryData<TenantWithRegion>(queryKey);
      return fetchTenantAccount(service, tenantId, previous);
    },
    enabled: options?.enabled !== false && !!accessToken && !!tenantId,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    refetch,
    isFetching,
  };
};
